import React from "react";
import { motion } from "framer-motion";
import { Flame, ChevronRight } from "lucide-react";
import { MENU_CATEGORIES, HERO_IMAGE } from "@/data/menu";

export default function MenuPage() {
  const [activeId, setActiveId] = React.useState(MENU_CATEGORIES[0].id);
  const sectionRefs = React.useRef({});

  // Scroll spy
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    MENU_CATEGORIES.forEach((c) => {
      const el = document.getElementById(c.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div data-testid="page-menu" className="bg-[#FBF8F3]">
      {/* HEADER */}
      <section className="relative h-[44vh] min-h-[380px] overflow-hidden pt-20 md:pt-0" data-testid="menu-hero">
        <img src={HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1025]/85 to-[#1A1025]/70" />
        <div className="relative h-full flex flex-col justify-end px-6 md:px-12 lg:px-24 max-w-[1500px] mx-auto pb-10 md:pb-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="eyebrow text-[#F5C518] mb-4 md:mb-5">The Menu</div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#FBF8F3] leading-[1] md:leading-[0.95] tracking-tight">
              A long, <span className="italic text-[#F5C518]">love-soaked</span> list.
            </h1>
          </motion.div>
        </div>
      </section>

      {/* SPICE NOTE */}
      <div
        data-testid="spice-note"
        className="bg-[#2A1B3D] text-[#FBF8F3] px-6 md:px-12 lg:px-24 py-4 text-center text-xs sm:text-sm tracking-wider flex items-center justify-center gap-3"
      >
        <Flame className="w-4 h-4 text-[#F5C518]" />
        <span>
          Most dishes can be prepared{" "}
          <strong className="text-[#F5C518]">Mild · Medium · Hot</strong> to your spice level.
        </span>
      </div>

      {/* BODY */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-20 max-w-[1500px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Sticky sidebar */}
          <aside
            className="lg:col-span-3 lg:sticky lg:top-28 self-start"
            data-testid="menu-sidebar"
          >
            <div className="eyebrow mb-5">Categories</div>

            {/* Mobile dropdown */}
            <div className="lg:hidden mb-2">
              <select
                data-testid="menu-category-select"
                className="w-full p-3 border border-[rgba(42,27,61,0.2)] bg-white text-[#2A1B3D] font-serif text-lg"
                value={activeId}
                onChange={(e) => jumpTo(e.target.value)}
              >
                {MENU_CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.title}</option>
                ))}
              </select>
            </div>

            <nav className="hidden lg:block border-l border-[rgba(42,27,61,0.15)] -ml-px">
              {MENU_CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  onClick={() => jumpTo(c.id)}
                  data-testid={`cat-link-${c.id}`}
                  className={`cat-link w-full text-left ${activeId === c.id ? "active" : ""}`}
                >
                  {c.title}
                </button>
              ))}
            </nav>
          </aside>

          {/* Items */}
          <div className="lg:col-span-9 space-y-24" data-testid="menu-content">
            {MENU_CATEGORIES.map((cat, ci) => (
              <section
                id={cat.id}
                key={cat.id}
                ref={(el) => (sectionRefs.current[cat.id] = el)}
                data-testid={`menu-section-${cat.id}`}
                className="scroll-mt-28"
              >
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="flex items-end justify-between gap-4 mb-10 pb-5 border-b border-[rgba(42,27,61,0.15)]"
                >
                  <div>
                    <div className="eyebrow mb-3">0{ci + 1} · Section</div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#2A1B3D] tracking-tight leading-tight">
                      {cat.title}
                    </h2>
                  </div>
                  <ChevronRight className="hidden sm:block w-5 h-5 text-[#E91E63]" />
                </motion.div>

                <ul className="grid sm:grid-cols-2 gap-x-12 gap-y-10">
                  {cat.items.map((item) => (
                    <li key={item.num} data-testid={`menu-item-${item.num}`} className="group">
                      <div className="flex items-baseline">
                        <span className="menu-num text-xl mr-3">{item.num}.</span>
                        <h3 className="font-serif text-xl sm:text-2xl text-[#2A1B3D] flex-shrink-0 group-hover:text-[#E91E63] transition-colors">
                          {item.name}
                        </h3>
                        <span className="dotted-leader" />
                        <span className="text-[#E91E63] font-bold text-sm whitespace-nowrap tracking-wider">
                          {item.price}
                        </span>
                      </div>
                      <p className="text-sm text-[#4A3F55] mt-1.5 leading-relaxed pl-8">
                        {item.desc}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            ))}

            <div className="pt-6 text-sm text-[#4A3F55]/80 italic border-t border-[rgba(42,27,61,0.12)]">
              Please let your server know about any food allergies. 18% gratuity will be
              added on all parties of 4 or more.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
